import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';

/**
 * SettingsService reactivo + persistente.
 *
 * `layout` sigue siendo un objeto mutable directamente (`settings.layout.x = y`),
 * como lo usa toda la app, pero ahora es un Proxy que:
 *   - persiste en localStorage el subconjunto de preferencias durables
 *     (colapsado/fijo/horizontal…) → el estado del sidebar sobrevive el reload;
 *   - emite `layoutChanges` en cada cambio para quien quiera reaccionar.
 *
 * El estado transitorio de UI (offcanvas/offsidebar abierto, hover) NO se
 * persiste a propósito (no queremos reabrirlo al recargar).
 */
@Injectable()
export class SettingsService {

  public user: any;
  public app: any;
  public layout: any;

  private static readonly STORAGE_KEY = 'sap.layout';
  // Solo estas claves se persisten (preferencias durables del usuario).
  private static readonly PERSISTED_KEYS = [
    'isFixed', 'isCollapsed', 'isCollapsedText', 'isBoxed', 'horizontal', 'hiddenFooter'
  ];

  private readonly layoutChange$ = new BehaviorSubject<any>(null);
  public readonly layoutChanges: Observable<any> = this.layoutChange$.asObservable();

  constructor() {

    // User Settings
    this.user = {
      username: 'User undefined',
      profile: 'Profile undefined',
      permits: [],
      picture: ''
    };

    // App Settings
    this.app = {
      name: 'SAP',
      description: 'Administrador de contenido SAP',
      year: ((new Date()).getFullYear())
    };

    // Layout Settings (defaults + preferencias persistidas)
    const defaults = {
      isFixed: true,
      isCollapsed: false,
      isBoxed: false,
      isRTL: false,
      horizontal: false,
      isFloat: false,
      asideHover: false,
      theme: null,
      asideScrollbar: false,
      isCollapsedText: false,
      useFullLayout: false,
      hiddenFooter: false,
      offsidebarOpen: false,
      asideToggled: false,
      viewAnimation: 'ng-fadeInUp'
    };

    const base = {...defaults, ...this.loadPersisted()};

    // Proxy: persiste + emite en cada set, manteniendo la API de mutación directa.
    this.layout = new Proxy(base, {
      set: (target, prop: string, value) => {
        target[prop] = value;
        if (SettingsService.PERSISTED_KEYS.includes(prop)) {
          this.persist(target);
        }
        this.layoutChange$.next(target);
        return true;
      }
    });
  }

  private loadPersisted(): any {
    try {
      return JSON.parse(localStorage.getItem(SettingsService.STORAGE_KEY) || '{}') || {};
    } catch {
      return {};
    }
  }

  private persist(target: any): void {
    try {
      const subset: any = {};
      for (const key of SettingsService.PERSISTED_KEYS) {
        subset[key] = target[key];
      }
      localStorage.setItem(SettingsService.STORAGE_KEY, JSON.stringify(subset));
    } catch {
      // localStorage no disponible (modo privado, etc.): ignorar, no es crítico.
    }
  }

  getAppSetting(name) {
    return name ? this.app[name] : this.app;
  }

  getUserSetting(name) {
    return name ? this.user[name] : this.user;
  }

  getLayoutSetting(name) {
    return name ? this.layout[name] : this.layout;
  }

  setAppSetting(name, value) {
    if (typeof this.app[name] !== 'undefined') {
      this.app[name] = value;
    }
  }

  setUserSetting(name, value) {
    if (typeof this.user[name] !== 'undefined') {
      this.user[name] = value;
    }
  }

  setLayoutSetting(name, value) {
    if (typeof this.layout[name] !== 'undefined') {
      return this.layout[name] = value;
    }
  }

  toggleLayoutSetting(name) {
    return this.setLayoutSetting(name, !this.getLayoutSetting(name));
  }

}
